import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../hooks/useApi';
import Icon from '../components/Icon';
import DateInput from '../components/DateInput';
import './ContratsList.css';

const STATUTS = [
  { value: '',           label: 'Tous les statuts' },
  { value: 'brouillon',  label: 'Brouillon' },
  { value: 'envoye',     label: 'Envoyé' },
  { value: 'en_revision',label: 'En révision' },
  { value: 'accepte',    label: 'Accepté' },
  { value: 'refuse',     label: 'Refusé' },
  { value: 'annule',     label: 'Annulé' },
  { value: 'complete',   label: 'Complété' },
];

const BADGE_CLASSES = {
  brouillon:   'badge badge--muted',
  envoye:      'badge badge--info',
  en_revision: 'badge badge--warning',
  accepte:     'badge badge--success',
  refuse:      'badge badge--danger',
  annule:      'badge badge--muted',
  complete:    'badge badge--gold',
};

const STATUT_LABELS = {
  brouillon:   'Brouillon',
  envoye:      'Envoyé',
  en_revision: 'En révision',
  accepte:     'Accepté',
  refuse:      'Refusé',
  annule:      'Annulé',
  complete:    'Complété',
};

function fmtDate(str) {
  if (!str) return '—';
  const MOIS = ['jan','fév','mar','avr','mai','jun','jul','aoû','sep','oct','nov','déc'];
  const d = new Date(str + 'T12:00:00');
  return `${d.getDate()} ${MOIS[d.getMonth()]} ${d.getFullYear()}`;
}

function fmtMontant(n) {
  if (n == null) return '—';
  return `${Number(n).toFixed(2)} $`;
}

// ── Skeleton ────────────────────────────────────────────────────────────────

function ContratsListSkeleton() {
  return (
    <div className="contrats-page">
      <div className="contrats-header">
        <div>
          <div className="skeleton" style={{ height: 36, width: 280, marginBottom: 8 }} />
          <div className="skeleton" style={{ height: 12, width: 140 }} />
        </div>
        <div className="skeleton" style={{ height: 36, width: 140, borderRadius: 'var(--radius-md)' }} />
      </div>
      <div className="card contrats-filtres">
        {[180, 130, 160].map((w, i) => (
          <div key={i} className="filtre-group">
            <div className="skeleton" style={{ height: 10, width: 60, marginBottom: 6 }} />
            <div className="skeleton" style={{ height: 36, width: w }} />
          </div>
        ))}
      </div>
      <div className="card table-wrapper">
        <table className="esc-table">
          <thead>
            <tr>
              {['N° Contrat','Client','Date','Pers.','Total','Acompte','Solde','Statut',''].map(h => (
                <th key={h}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {[0,1,2,3].map(i => (
              <tr key={i}>
                {[120,160,100,50,80,80,80,70,80].map((w, j) => (
                  <td key={j}><div className="skeleton" style={{ height: 14, width: w }} /></td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function ContratsList() {
  const navigate = useNavigate();
  const [contrats, setContrats] = useState([]);
  const [clients, setClients]   = useState([]);
  const [loading, setLoading]   = useState(true);
  const [erreur, setErreur]     = useState('');

  const [filtreStatut,    setFiltreStatut]   = useState('');
  const [filtreAnnee,     setFiltreAnnee]    = useState(String(new Date().getFullYear()));
  const [filtreClient,    setFiltreClient]   = useState('');

  const [showModal,       setShowModal]      = useState(false);
  const [showModalClient, setShowModalClient]= useState(false);

  const [form, setForm] = useState({
    client_id: '', date_evenement: '', nb_personnes: 50,
  });
  const [formClient, setFormClient] = useState({
    entreprise: '', contact_prenom: '', contact_nom: '',
    contact_email: '', contact_tel: '',
  });
  const [saving, setSaving] = useState(false);

  const anneesCourantes = [String(new Date().getFullYear()), String(new Date().getFullYear() - 1)];

  const charger = useCallback(async () => {
    try {
      setLoading(true);
      setErreur('');
      const params = new URLSearchParams();
      if (filtreStatut) params.append('statut', filtreStatut);
      if (filtreAnnee)  params.append('annee', filtreAnnee);
      if (filtreClient) params.append('client_id', filtreClient);

      const [listeContrats, listeClients] = await Promise.all([
        api.get(`/corpo/contrats?${params}`),
        api.get('/corpo/clients'),
      ]);
      setContrats(Array.isArray(listeContrats) ? listeContrats : []);
      setClients(Array.isArray(listeClients) ? listeClients : []);
    } catch (e) {
      setErreur(e.message);
    } finally {
      setLoading(false);
    }
  }, [filtreStatut, filtreAnnee, filtreClient]);

  useEffect(() => { charger(); }, [charger]);

  async function creerContrat(e) {
    e.preventDefault();
    if (!form.client_id || !form.date_evenement) return;
    try {
      setSaving(true);
      const nouveau = await api.post('/corpo/contrats', {
        client_id: parseInt(form.client_id),
        date_evenement: form.date_evenement,
        nb_personnes: parseInt(form.nb_personnes) || 50,
      });
      setShowModal(false);
      navigate(`/corpo/contrats/${nouveau.id}`);
    } catch (e) {
      alert('Erreur : ' + e.message);
    } finally {
      setSaving(false);
    }
  }

  async function creerClient(e) {
    e.preventDefault();
    try {
      setSaving(true);
      const nouveau = await api.post('/corpo/clients', formClient);
      setClients(prev => [...prev, nouveau]);
      setForm(f => ({ ...f, client_id: String(nouveau.id) }));
      setShowModalClient(false);
      setFormClient({ entreprise: '', contact_prenom: '', contact_nom: '', contact_email: '', contact_tel: '' });
    } catch (e) {
      alert('Erreur : ' + e.message);
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <ContratsListSkeleton />;

  return (
    <div className="contrats-page">
      {/* En-tête */}
      <div className="contrats-header">
        <div>
          <h1 className="page-title">Contrats Corporatifs</h1>
          <p className="page-subtitle muted">{contrats.length} contrat{contrats.length !== 1 ? 's' : ''}</p>
        </div>
        <button className="btn btn-primary" onClick={() => setShowModal(true)}>
          <Icon name="ajouter" size={15} />
          Nouveau contrat
        </button>
      </div>

      {/* Filtres */}
      <div className="contrats-filtres card">
        <div className="filtre-group">
          <label>Statut</label>
          <select value={filtreStatut} onChange={e => setFiltreStatut(e.target.value)}>
            {STATUTS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
        </div>
        <div className="filtre-group">
          <label>Année</label>
          <select value={filtreAnnee} onChange={e => setFiltreAnnee(e.target.value)}>
            <option value="">Toutes</option>
            {anneesCourantes.map(a => <option key={a} value={a}>{a}</option>)}
          </select>
        </div>
        <div className="filtre-group">
          <label>Client</label>
          <select value={filtreClient} onChange={e => setFiltreClient(e.target.value)}>
            <option value="">Tous les clients</option>
            {clients.map(c => (
              <option key={c.id} value={c.id}>{c.entreprise}</option>
            ))}
          </select>
        </div>
        <button className="btn btn-ghost" onClick={charger} title="Actualiser">
          <Icon name="rafraichir" size={15} />
        </button>
      </div>

      {/* Erreur */}
      {erreur && (
        <div className="error-inline" style={{ marginBottom: 'var(--space-4)' }}>
          <Icon name="erreur" size={16} />
          {erreur}
        </div>
      )}

      {/* Tableau / État vide */}
      {contrats.length === 0 ? (
        <div className="empty-state card">
          <Icon name="document" size={40} />
          <p className="empty-state__titre">Aucun contrat trouvé</p>
          <p>Ajustez les filtres ou créez votre premier contrat.</p>
          <button className="btn btn-primary" onClick={() => setShowModal(true)}>
            <Icon name="ajouter" size={15} />
            Créer un contrat
          </button>
        </div>
      ) : (
        <div className="card table-wrapper rise-in">
          <table className="esc-table">
            <thead>
              <tr>
                <th>N° Contrat</th>
                <th>Client</th>
                <th>Date événement</th>
                <th>Pers.</th>
                <th>Total TTC</th>
                <th>Acompte</th>
                <th>Solde</th>
                <th>Statut</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {contrats.map(c => (
                <tr key={c.id} className="contrat-row" onClick={() => navigate(`/corpo/contrats/${c.id}`)}>
                  <td className="num-contrat">
                    {c.numero_contrat}
                    {c.nb_non_lus > 0 && (
                      <span className="badge-count">{c.nb_non_lus}</span>
                    )}
                    {c.notes_internes?.includes('Gravity Forms') && (
                      <span className="badge-gf" style={{ marginLeft: 6 }}>GF</span>
                    )}
                  </td>
                  <td>
                    <div className="client-cell">
                      <span className="client-name">{c.entreprise}</span>
                      <span className="client-contact muted">{c.contact_prenom} {c.contact_nom}</span>
                    </div>
                  </td>
                  <td>{fmtDate(c.date_evenement)}</td>
                  <td className="centered">{c.nb_personnes}</td>
                  <td className="montant">{fmtMontant(c.total_ttc)}</td>
                  <td className={`montant ${c.acompte_paye ? 'paid' : 'unpaid'}`}>
                    {fmtMontant(c.acompte_montant)}
                    {c.acompte_paye && (
                      <Icon name="check" size={12} style={{ marginLeft: 4, color: 'var(--success)' }} />
                    )}
                  </td>
                  <td className={`montant ${c.solde_paye ? 'paid' : 'unpaid'}`}>
                    {fmtMontant(c.solde_montant)}
                    {c.solde_paye && (
                      <Icon name="check" size={12} style={{ marginLeft: 4, color: 'var(--success)' }} />
                    )}
                  </td>
                  <td>
                    <span className={BADGE_CLASSES[c.statut] || 'badge badge--muted'}>
                      {STATUT_LABELS[c.statut] || c.statut}
                    </span>
                  </td>
                  <td onClick={e => e.stopPropagation()}>
                    <button
                      className="btn btn-ghost btn--sm"
                      onClick={() => navigate(`/corpo/contrats/${c.id}`)}
                    >
                      Ouvrir
                      <Icon name="chevron" size={13} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal création contrat */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-box" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Nouveau contrat</h3>
              <button className="modal-close" onClick={() => setShowModal(false)}>
                <Icon name="fermer" size={16} />
              </button>
            </div>
            <form onSubmit={creerContrat} className="modal-form">
              <div className="form-group">
                <label>Client *</label>
                <div className="client-select-row">
                  <select
                    value={form.client_id}
                    onChange={e => setForm(f => ({ ...f, client_id: e.target.value }))}
                    required
                  >
                    <option value="">Sélectionner un client…</option>
                    {clients.map(c => (
                      <option key={c.id} value={c.id}>{c.entreprise} — {c.contact_prenom} {c.contact_nom}</option>
                    ))}
                  </select>
                  <button type="button" className="btn btn-ghost btn--sm" onClick={() => setShowModalClient(true)}>
                    <Icon name="ajouter" size={13} />
                    Nouveau
                  </button>
                </div>
              </div>
              <div className="form-group">
                <label>Date de l'événement *</label>
                <DateInput
                  value={form.date_evenement}
                  onChange={v => setForm(f => ({ ...f, date_evenement: v }))}
                  required
                />
              </div>
              <div className="form-group">
                <label>Nombre de personnes</label>
                <input
                  type="number"
                  min="1"
                  value={form.nb_personnes}
                  onChange={e => setForm(f => ({ ...f, nb_personnes: e.target.value }))}
                />
              </div>
              <div className="modal-actions">
                <button type="button" className="btn btn-ghost" onClick={() => setShowModal(false)}>Annuler</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? 'Création…' : 'Créer le contrat'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal création client rapide */}
      {showModalClient && (
        <div className="modal-overlay" onClick={() => setShowModalClient(false)}>
          <div className="modal-box" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Nouveau client</h3>
              <button className="modal-close" onClick={() => setShowModalClient(false)}>
                <Icon name="fermer" size={16} />
              </button>
            </div>
            <form onSubmit={creerClient} className="modal-form">
              <div className="form-group">
                <label>Entreprise *</label>
                <input
                  type="text"
                  value={formClient.entreprise}
                  onChange={e => setFormClient(f => ({ ...f, entreprise: e.target.value }))}
                  required
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Prénom *</label>
                  <input
                    type="text"
                    value={formClient.contact_prenom}
                    onChange={e => setFormClient(f => ({ ...f, contact_prenom: e.target.value }))}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Nom *</label>
                  <input
                    type="text"
                    value={formClient.contact_nom}
                    onChange={e => setFormClient(f => ({ ...f, contact_nom: e.target.value }))}
                    required
                  />
                </div>
              </div>
              <div className="form-group">
                <label>Courriel *</label>
                <input
                  type="email"
                  value={formClient.contact_email}
                  onChange={e => setFormClient(f => ({ ...f, contact_email: e.target.value }))}
                  required
                />
              </div>
              <div className="form-group">
                <label>Téléphone</label>
                <input
                  type="tel"
                  value={formClient.contact_tel}
                  onChange={e => setFormClient(f => ({ ...f, contact_tel: e.target.value }))}
                />
              </div>
              <div className="modal-actions">
                <button type="button" className="btn btn-ghost" onClick={() => setShowModalClient(false)}>Annuler</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? 'Création…' : 'Créer le client'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
